import React, { useEffect, useState } from 'react';
import { CheckIcon, XIcon, EyeIcon, ShieldCheckIcon, SaveIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { useData } from '../../contexts/DataContext';
import { Role, Permission, PermissionLevel } from '../../types';
export function Permissions() {
  const {
    roles,
    permissions,
    getPermissionsByRole,
    updatePermissions
  } = useData();
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [rolePermissions, setRolePermissions] = useState<Record<string, Permission>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const modules = [{
    key: 'dashboard',
    label: 'Panel de Control'
  }, {
    key: 'patients',
    label: 'Pacientes'
  }, {
    key: 'doctors',
    label: 'Doctores'
  }, {
    key: 'appointments',
    label: 'Citas'
  }, {
    key: 'clinicalHistory',
    label: 'Historial Clínico'
  }, {
    key: 'services',
    label: 'Servicios'
  }, {
    key: 'insurance',
    label: 'Seguros'
  }, {
    key: 'specialties',
    label: 'Especialidades'
  }, {
    key: 'contacts',
    label: 'Contactos'
  }, {
    key: 'companies',
    label: 'Empresas'
  }, {
    key: 'opportunities',
    label: 'Oportunidades'
  }, {
    key: 'settings',
    label: 'Configuración'
  }];
  const roleOptions = roles.map(r => ({
    value: r.id,
    label: r.displayName
  }));
  useEffect(() => {
    if (selectedRoleId) {
      const perms = getPermissionsByRole(selectedRoleId);
      const permsMap: Record<string, Permission> = {};
      perms.forEach(p => {
        permsMap[p.module] = p;
      });
      setRolePermissions(permsMap);
      setHasChanges(false);
    }
  }, [selectedRoleId, permissions]);
  const getPermissionIcon = (level: PermissionLevel) => {
    switch (level) {
      case 'full':
        return <CheckIcon className="w-5 h-5 text-green-500 dark:text-green-400" data-id="element-1311" />;
      case 'read':
        return <EyeIcon className="w-5 h-5 text-blue-500 dark:text-blue-400" data-id="element-1312" />;
      case 'own':
        return <ShieldCheckIcon className="w-5 h-5 text-amber-500 dark:text-amber-400" data-id="element-1313" />;
      case 'none':
        return <XIcon className="w-5 h-5 text-red-500 dark:text-red-400" data-id="element-1314" />;
    }
  };
  const getPermissionLabel = (level: PermissionLevel) => {
    switch (level) {
      case 'full':
        return 'Completo';
      case 'read':
        return 'Solo lectura';
      case 'own':
        return 'Propias';
      case 'none':
        return 'Sin acceso';
    }
  };
  const cyclePermission = (module: string) => {
    const levels: PermissionLevel[] = ['none', 'read', 'own', 'full'];
    const currentLevel = rolePermissions[module]?.permissionLevel || 'none';
    const currentIndex = levels.indexOf(currentLevel);
    const nextIndex = (currentIndex + 1) % levels.length;
    const nextLevel = levels[nextIndex];
    // Update permission level and corresponding flags
    const canCreate = nextLevel === 'full';
    const canRead = nextLevel !== 'none';
    const canUpdate = nextLevel === 'full' || nextLevel === 'own';
    const canDelete = nextLevel === 'full';
    setRolePermissions(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        permissionLevel: nextLevel,
        canCreate,
        canRead,
        canUpdate,
        canDelete
      }
    }));
    setHasChanges(true);
  };
  const handleSave = async () => {
    if (!selectedRoleId) return;
    try {
      const permissionsToUpdate = Object.values(rolePermissions);
      await updatePermissions(selectedRoleId, permissionsToUpdate);
      setHasChanges(false);
      setSaveMessage('Permisos guardados correctamente');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving permissions:', error);
      setSaveMessage('Error al guardar permisos');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };
  const selectedRole = roles.find(r => r.id === selectedRoleId);
  return <div className="space-y-6" data-id="element-1315">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1316">
        <div data-id="element-1317">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-1318">
            <ShieldCheckIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" data-id="element-1319" />
            Permisos
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-1320">
            Configura los permisos de acceso para cada rol
          </p>
        </div>
        {hasChanges && <Button leftIcon={<SaveIcon className="w-4 h-4" data-id="element-1322" />} onClick={handleSave} data-id="element-1321">
            Guardar Cambios
          </Button>}
      </div>

      {saveMessage && <div className={`p-3 rounded-lg text-sm ${saveMessage.includes('Error') ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400' : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400'}`} data-id="element-1323">
          {saveMessage}
        </div>}

      {/* Role Selector */}
      <Card data-id="element-1324">
        <div className="p-4" data-id="element-1325">
          <Select label="Seleccionar Rol" options={roleOptions} value={selectedRoleId} onChange={setSelectedRoleId} placeholder="Seleccione un rol para configurar sus permisos" data-id="element-1326" />
          {selectedRole && <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg" data-id="element-1327">
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1328">
                <span className="font-medium text-gray-900 dark:text-white" data-id="element-1329">
                  {selectedRole.displayName}:
                </span>{' '}
                {selectedRole.description || 'Sin descripción'}
              </p>
            </div>}
        </div>
      </Card>

      {selectedRoleId && <>
          {/* Legend */}
          <Card data-id="element-1330">
            <div className="p-4" data-id="element-1331">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3" data-id="element-1332">
                Leyenda de Permisos
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1333">
                <div className="flex items-center gap-2" data-id="element-1334">
                  <CheckIcon className="w-5 h-5 text-green-500" data-id="element-1335" />
                  <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1336">
                    Acceso completo (CRUD)
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1337">
                  <EyeIcon className="w-5 h-5 text-blue-500" data-id="element-1338" />
                  <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1339">
                    Solo lectura
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1340">
                  <ShieldCheckIcon className="w-5 h-5 text-amber-500" data-id="element-1341" />
                  <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1342">
                    Solo propias
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1343">
                  <XIcon className="w-5 h-5 text-red-500" data-id="element-1344" />
                  <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1345">
                    Sin acceso
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* Permissions Table */}
          <Card data-id="element-1346">
            <div className="overflow-x-auto" data-id="element-1347">
              <table className="w-full" data-id="element-1348">
                <thead data-id="element-1349">
                  <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-1350">
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50" data-id="element-1351">
                      Módulo
                    </th>
                    <th className="text-center p-4 font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50" data-id="element-1352">
                      Nivel de Acceso
                    </th>
                    <th className="text-center p-4 font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50" data-id="element-1353">
                      Crear
                    </th>
                    <th className="text-center p-4 font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50" data-id="element-1354">
                      Leer
                    </th>
                    <th className="text-center p-4 font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50" data-id="element-1355">
                      Editar
                    </th>
                    <th className="text-center p-4 font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50" data-id="element-1356">
                      Eliminar
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-1357">
                  {modules.map((module, index) => {
                const permission = rolePermissions[module.key];
                const level = permission?.permissionLevel || 'none';
                return <tr key={module.key} className={`
                          border-b border-gray-200 dark:border-gray-700
                          ${index % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/30'}
                        `} data-id="element-1358">
                        <td className="p-4 font-medium text-gray-900 dark:text-white" data-id="element-1359">
                          {module.label}
                        </td>
                        <td className="p-4 text-center" data-id="element-1360">
                          <button onClick={() => cyclePermission(module.key)} className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title={`Click para cambiar - Actual: ${getPermissionLabel(level)}`} data-id="element-1361">
                            {getPermissionIcon(level)}
                            <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-1362">
                              {getPermissionLabel(level)}
                            </span>
                          </button>
                        </td>
                        <td className="p-4 text-center" data-id="element-1363">
                          {permission?.canCreate ? <CheckIcon className="w-5 h-5 text-green-500 mx-auto" data-id="element-1364" /> : <XIcon className="w-5 h-5 text-gray-300 dark:text-gray-600 mx-auto" data-id="element-1365" />}
                        </td>
                        <td className="p-4 text-center" data-id="element-1366">
                          {permission?.canRead ? <CheckIcon className="w-5 h-5 text-green-500 mx-auto" data-id="element-1367" /> : <XIcon className="w-5 h-5 text-gray-300 dark:text-gray-600 mx-auto" data-id="element-1368" />}
                        </td>
                        <td className="p-4 text-center" data-id="element-1369">
                          {permission?.canUpdate ? <CheckIcon className="w-5 h-5 text-green-500 mx-auto" data-id="element-1370" /> : <XIcon className="w-5 h-5 text-gray-300 dark:text-gray-600 mx-auto" data-id="element-1371" />}
                        </td>
                        <td className="p-4 text-center" data-id="element-1372">
                          {permission?.canDelete ? <CheckIcon className="w-5 h-5 text-green-500 mx-auto" data-id="element-1373" /> : <XIcon className="w-5 h-5 text-gray-300 dark:text-gray-600 mx-auto" data-id="element-1374" />}
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </Card>
        </>}

      {!selectedRoleId && <Card data-id="element-1375">
          <div className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-1376">
            <ShieldCheckIcon className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" data-id="element-1377" />
            <p data-id="element-1378">Selecciona un rol para configurar sus permisos</p>
          </div>
        </Card>}
    </div>;
}